/**
 * globo-gst-price.js
 * Keeps the custom exc./inc. GST price display intact when Globo Product Options
 * dynamically recalculates the price.
 *
 * Globo Product Options overwrites the content of any element with class `.price`
 * with its recalculated tax-inclusive total (on page load and every time a priced
 * option is toggled). Our product price block (blocks/price.liquid) renders two
 * columns whose value spans BOTH carry `.price`:
 *   - `.price-gst-primary`          -> exc. GST value + a nested `.price-gst-label`
 *   - `.price-gst-secondary-price`  -> inc. GST value + a nested `.price-gst-label`
 * So Globo clobbers both columns to the same inc-GST value and strips the labels,
 * which is what makes the "exc. GST" / "inc. GST" text drop off and the pricing
 * look broken on Globo-option products (e.g. the calibration add-on).
 *
 * This watches the product price and, whenever Globo rewrites it, re-derives the
 * exc-GST value from the inc-GST total Globo produced (matching the Liquid maths in
 * blocks/price.liquid: `price | times: 10 | divided_by: 11`, integer-cent division)
 * and restores both labelled columns. It is guarded against MutationObserver
 * feedback loops and no-ops on products without the GST columns.
 *
 * Verified live against Globo on the production theme, 2026-07-23 (WIL-231).
 */
(function () {
  var GST_NUM = 10;
  var GST_DEN = 11; // exc-GST = inc-GST * 10 / 11 (AU 10% GST)

  function parseMoney(value) {
    if (value == null) return null;
    var n = parseFloat(String(value).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  }

  function formatMoney(amount) {
    return '$' + amount.toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function textBeforeLabel(span, hasLabel) {
    if (hasLabel && span.childNodes[0]) return span.childNodes[0].textContent;
    return span.textContent;
  }

  function restore(productPrice) {
    var primary = productPrice.querySelector('.price-gst-primary');
    var secondary = productPrice.querySelector('.price-gst-secondary-price');
    if (!primary || !secondary) return;

    var primaryHasLabel = !!primary.querySelector('.price-gst-label');
    var secondaryHasLabel = !!secondary.querySelector('.price-gst-label');

    // The inc-GST total is whatever Globo last wrote into the .price spans.
    var incString = (textBeforeLabel(secondary, secondaryHasLabel) || '').trim();
    var incTotal = parseMoney(incString);
    if (incTotal == null) return;

    var incCents = Math.round(incTotal * 100);
    var excCents = Math.floor((incCents * GST_NUM) / GST_DEN); // matches the Liquid integer division
    var excString = formatMoney(excCents / 100);

    // Only act when Globo has actually damaged the display: a label is missing, or
    // both columns are showing the same value (exc == inc).
    var primaryValue = parseMoney(textBeforeLabel(primary, primaryHasLabel));
    var isDamaged =
      !primaryHasLabel ||
      !secondaryHasLabel ||
      (primaryValue != null && Math.abs(primaryValue - incTotal) < 0.005);
    if (!isDamaged) return;

    primary.innerHTML = excString + '<span class="price-gst-label"> exc. GST</span>';
    secondary.innerHTML = incString + '<span class="price-gst-label"> inc. GST</span>';
  }

  function activate() {
    var columns = document.querySelector('.price-gst-columns');
    if (!columns) return false;
    var productPrice = columns.closest('product-price') || columns.parentElement;
    if (!productPrice) return false;

    var observer = new MutationObserver(function () {
      // Disconnect while we write so our own restore doesn't retrigger the observer.
      observer.disconnect();
      restore(productPrice);
      observer.observe(productPrice, { childList: true, subtree: true, characterData: true });
    });

    restore(productPrice); // repair any clobber that already happened before we loaded
    observer.observe(productPrice, { childList: true, subtree: true, characterData: true });
    return true;
  }

  function boot() {
    if (activate()) return;
    // The price block or Globo's widget can render late — retry briefly, then give up.
    var attempts = 0;
    var timer = setInterval(function () {
      if (activate() || ++attempts > 20) clearInterval(timer);
    }, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
